import { isInputValueIncluded } from "components/Form/components/Autocomplete/Autocomplete.service";
import { AsyncSelect } from "components/Form/components/Autocomplete/components/AsyncSelect";
import { DropdownContainer } from "components/Form/components/Autocomplete/components/DropdownContainer";
import { SearchableSelect } from "components/Form/components/Autocomplete/components/SearchableSelect";
import { SimpleSelect } from "components/Form/components/Autocomplete/components/SimpleSelect";
import { useAsyncSelect } from "components/Form/components/Autocomplete/useAsyncSelect";
import { useCachedResourceOptions } from "components/Form/components/Autocomplete/useCachedResourceOptions";
import { useAutocompleteMenuPortal } from "components/Form/components/Autocomplete/AutocompleteMenuPortalContext";
import { RecordType } from "graphql/generated/graphql";
import { MdiReactIconComponentType } from "mdi-react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import Select from "react-select/dist/declarations/src/Select";
import { getListFromGraphql, SingleField, useOutsideAutocompleteClick } from "utils/utils";
import "./Autocomplete.scss";
import {
    AutocompleteSearchMenuList,
    ClearIndicator,
    DropdownIndicator,
    GroupLabel,
    IconOptionGenerator,
    LoadingMessage,
    NoOptionsMessage,
    OptionLabel,
    SingleValueWithIconReplacement,
    ValueContainer,
} from "./ReactSelectComponents";

export interface AutocompleteOption {
    value: any;
    label: string;
    icon?: any;
    iconName?: string;
    is_readonly?: boolean;
    type?: Lowercase<RecordType>;
}

export interface GroupOptions {
    label: string;
    options: AutocompleteOption[];
}

export interface GroupAutocompleteOptions {
    groupOptions: GroupOptions[];
    flatList: AutocompleteOption[];
}

export interface AutocompleteOptionsProps {
    resource?: string; // TODO remove this, this Autocomplete should not know anything about resources
    enumName?: string; // TODO remove this, this Autocomplete should not know anything about resources
    list?: AutocompleteOption[];
    hasNameAsOptionValue?: boolean;
    // A single clause, or an `and`-group of clauses the service pushes verbatim into its
    // outer filter `{and:[…]}` (e.g. object_template scope + "back-reference is unset").
    queryField?: SingleField | { and: SingleField[] };
    group?: GroupAutocompleteOptions;
    iconReplacement?: MdiReactIconComponentType;
    filterOptions?: (option: AutocompleteOption) => boolean;
    resourceBody?: string;
    isCreatable?: boolean;
    applyActionOnCreate?: () => void;
    applyActionOnChange?: () => void;
    hasInitialValuesLoaded?: boolean;
}

export const getSelectedValue = ({
    defaultValue,
    value,
    options,
    isDropdown,
}: {
    defaultValue?: AutocompleteOption;
    value: any;
    options?: AutocompleteOption[];
    isDropdown?: boolean;
}) => {
    if (defaultValue !== undefined && !value) {
        return defaultValue;
    }

    // if there is an input value provided, try to map the id it to the corresponding label
    // if this fails, just display the value itself
    if (value != null && value !== "") {
        // if the option is -1 and is a dropdown, "Invalid choice will be displayed"
        if (value === -1 && isDropdown) {
            return { value: -1, label: "Invalid choice" };
        }

        // if a list of options is provided, use it instead of the state
        const foundOption = (options ?? []).find((item) => item.value === value);

        // otherwise it returns the found option or fallback option
        return foundOption ?? { value, label: value };
    }

    // otherwise, there is no selection to be displayed
    return null;
};

// this component only supports single selections
// if multiselect is needed, it may be helpful to view single select as
// a special case of multiselect, where there is only 1 select item allowed
// in other words, the multiselect component should be able to handle single select as well
// to achieve this, one could start by changing the single select to return an array
// with only one result in it.
export const Autocomplete = ({
    name,
    options: optionsProp = {},
    defaultValue,
    value,
    onChange,
    onBlur = () => {},
    additionalProps = {},
    isDisabled = false,
}: {
    name: string;
    options: AutocompleteOptionsProps;
    defaultValue?: AutocompleteOption;
    value?: any;
    onChange: (selectedValue: any) => void;
    onBlur?: () => void;
    onFocus?: () => void;
    additionalProps?: any;
    isDisabled?: boolean;
}) => {
    const [isOpened, setOpened] = useState(false);
    const menuRef = useRef<Select>();

    // For reference pickers whose full list is already kept live in Redux
    // (project / bundle / role), serve them from the store as static options
    // instead of the per-open GraphQL fetch. No-op for every other picker.
    const options = useCachedResourceOptions(optionsProp);

    // Render the option menu in a flip-aware MUI Popper anchored to the field
    // (instead of the inline, always-downward CSS menu). Needed in two cases:
    // (1) inside a record-prompt modal, to escape the modal's `overflow-y: auto`
    //     clip (see AutocompleteMenuPortalContext — provided via context); and
    // (2) for a picker sitting near the viewport bottom (e.g. the comment
    //     composer's assignee picker), where the inline menu would open downward
    //     off-screen — the Popper's `flip` modifier opens it upward when there is
    //     no room below. Opt a specific picker in with `additionalProps.menuPortal`.
    // `additionalProps.menuInModal` is the legacy explicit alias, still honoured.
    const menuPortalFromContext = useAutocompleteMenuPortal();
    const menuInModal =
        additionalProps?.menuInModal || additionalProps?.menuPortal || menuPortalFromContext;

    const { list, group, resource: resourceName, enumName } = options;

    const { applyActionOnCreate, applyActionOnChange, isCreatable } = additionalProps ?? {};

    const optionList = useMemo(() => list ?? group?.flatList, [group?.flatList, list]);

    const enumOptionList = useMemo(() => {
        if (enumName) {
            const optionList = getListFromGraphql(enumName) || [];
            const mappedOptions = [...optionList].map((item) => ({
                value: item,
                label: item,
                link: null,
                icon: null,
            }));

            const sortedOptions = mappedOptions.sort((a, b) => {
                return a.label.localeCompare(b.label);
            });

            return sortedOptions;
        }
    }, [enumName]);

    const staticOptions = useMemo(() => {
        if (optionList === undefined && enumOptionList === undefined) {
            return;
        }
        const copyOptions = [...(optionList ?? enumOptionList ?? [])];
        if (
            !group?.flatList &&
            defaultValue &&
            !copyOptions.find((option) => option.value === defaultValue.value)
        ) {
            copyOptions.unshift(defaultValue);
        }
        return copyOptions;
    }, [optionList, enumOptionList, group?.flatList, defaultValue]);
    const iri = useMemo(
        () => `${name || additionalProps?.customKey}-${resourceName}`,
        [additionalProps?.customKey, name, resourceName]
    );
    const {
        isLoading,
        term,
        setTerm,
        hasMore,
        loadOptions,
        handleMenuOpen,
        currentPageRef,
        dynamicOptions,
        hasLoadedOptions,
        dynamicSelectedOption,
    } = useAsyncSelect(
        options,
        value,
        iri,
        defaultValue,
        additionalProps?.hasInitialValuesLoaded,
        additionalProps?.initialAutocompleteOption
    );

    const [currentScrollPosition, setCurrentScrollPosition] = useState(0);

    const toggleOpened = useCallback(() => {
        const newOpenState = !isOpened;
        setOpened(newOpenState);
        setCurrentScrollPosition(0);
        if (newOpenState && resourceName) {
            handleMenuOpen();
        }
    }, [handleMenuOpen, isOpened, resourceName]);
    const noIcons =
        staticOptions !== undefined
            ? staticOptions.some((item) => item.icon)
            : dynamicOptions?.some((item) => item.icon) ?? false;

    // generate the option component
    // passing the default value is necessary to properly highlight it
    // noIcons mode is used to fix weird indentation when no icons are present
    const IconOption = useMemo(
        () => IconOptionGenerator(defaultValue, noIcons, options?.iconReplacement),
        [defaultValue, noIcons, options?.iconReplacement]
    );

    const selectedValue = useMemo(() => {
        return staticOptions === undefined
            ? dynamicSelectedOption
            : getSelectedValue({
                  value,
                  defaultValue,
                  isDropdown: additionalProps.isDropdown,
                  options: staticOptions,
              });
    }, [dynamicSelectedOption, value, defaultValue, additionalProps.isDropdown, staticOptions]);

    const onChangeValue = useCallback(
        (selectedOption) => {
            if (selectedOption) {
                onChange(selectedOption.value);
            } else {
                onChange(null);
            }
            applyActionOnChange?.();
            isOpened && toggleOpened();
        },
        [applyActionOnChange, isOpened, onChange, toggleOpened]
    );

    const isSearchInputHidden = useMemo(
        () => staticOptions && staticOptions.length < 5 && !isCreatable,
        [isCreatable, staticOptions]
    );

    // remap config objects for react-select
    const configMapper = useCallback(
        (config) => {
            // shallow copy is okay since we only transform top level properties
            const newConfig = { ...config };
            if ("clearable" in newConfig) {
                newConfig.isClearable = newConfig.clearable;
                delete newConfig.clearable;
            }
            if ("disabled" in newConfig) {
                newConfig.isDisabled = newConfig.disabled;
                delete newConfig.disabled;
            }
            if (!isSearchInputHidden && "placeholder" in newConfig) {
                delete newConfig.placeholder;
            }
            if ("customKey" in newConfig) {
                delete newConfig.customKey;
            }
            if ("fieldWidth" in newConfig) {
                delete newConfig.fieldWidth;
            }
            if ("maxFieldWidth" in newConfig) {
                delete newConfig.maxFieldWidth;
            }
            if ("menuInModal" in newConfig) {
                delete newConfig.menuInModal;
            }
            // `menuPortal` is our own opt-in flag (routes to the Popper path),
            // not a react-select prop — strip it so it is not forwarded.
            // `menuPlacement` IS a valid react-select prop and is intentionally
            // left in so the <5-option SimpleSelect fallback can flip ("auto").
            if ("menuPortal" in newConfig) {
                delete newConfig.menuPortal;
            }

            return newConfig;
        },
        [isSearchInputHidden]
    );

    useEffect(() => {
        if (isOpened && menuRef.current) {
            menuRef.current.focus();
        }
    }, [isOpened]);

    const uniqueKey = `${additionalProps?.recordId ? `${additionalProps?.recordId}-` : ""}${
        additionalProps?.customKey ?? resourceName ?? enumName ?? ""
    }-${name}`;

    useOutsideAutocompleteClick(menuRef, uniqueKey, () => {
        if (isOpened) {
            setOpened(false);
        }
    });

    const formatGroupLabel = useMemo(
        () => (group ? (data) => <GroupLabel label={data.label} /> : null),
        [group]
    );

    const sharedProps = useMemo(
        () => ({
            uniqueKey,
            menuPortalTarget: menuInModal ? document.body : null,
            menuPosition: menuInModal ? "fixed" : undefined,
            unstyled: true,
            onBlur,
            onChange: onChangeValue,
            iconReplacement: options?.iconReplacement,
            components: {
                ClearIndicator,
                DropdownIndicator,
                SingleValue: SingleValueWithIconReplacement,
                NoOptionsMessage,
                LoadingMessage,
                Option: IconOption,
            },
            isClearable: additionalProps?.clearable !== false,
            id: name,
            name: name,
            placeholder: "",
            classNamePrefix: "react-select",
            styles: {
                container: () => ({
                    width: additionalProps?.fieldWidth ?? "100%",
                    maxWidth: additionalProps?.maxFieldWidth,
                    position: "relative",
                }),
                menu: () => ({
                    // The search-field control (radius 8px 8px 0 0, flat bottom)
                    // is designed to sit flush against the option list below it.
                    // A margin here opens a visible gap between the search field
                    // and the actual dropdown list — connect them flush instead.
                    marginTop: "0",
                }),
                // In-modal: lift the portaled menu above the modal layer (root
                // z-index 2000) so it is not painted behind the dialog.
                ...(menuInModal
                    ? { menuPortal: (base) => ({ ...base, zIndex: 2200 }) }
                    : {}),
            },
            getOptionLabel: (option: AutocompleteOption) => (
                <OptionLabel
                    option={option}
                    showReadOnlyChip={additionalProps?.showReadOnlyChip}
                    defaultValue={defaultValue}
                    iconReplacement={options?.iconReplacement}
                    removeOption={
                        additionalProps?.iconOnlyComponent &&
                        additionalProps?.clearable !== false &&
                        selectedValue?.value === option?.value
                            ? (e) => {
                                  e?.stopPropagation();
                                  e?.preventDefault();
                                  currentPageRef.current = -1;
                                  onChangeValue(null);
                              }
                            : undefined
                    }
                />
            ),
            formatGroupLabel,
            ...configMapper(additionalProps),
            isDisabled,
        }),
        [
            IconOption,
            additionalProps,
            configMapper,
            currentPageRef,
            defaultValue,
            formatGroupLabel,
            isDisabled,
            menuInModal,
            name,
            onBlur,
            onChangeValue,
            options?.iconReplacement,
            selectedValue?.value,
            uniqueKey,
        ]
    );

    const hasSelectedValue = useMemo(() => {
        const hasValue = (value) => value != null && value !== "";
        return (
            selectedValue != null &&
            (hasValue(selectedValue?.label) || hasValue(selectedValue?.value))
        );
    }, [selectedValue]);

    const isSelectedOptionHidden = useMemo(
        () =>
            term?.trim() && hasSelectedValue
                ? !isInputValueIncluded(selectedValue as AutocompleteOption, term)
                : false,
        [hasSelectedValue, selectedValue, term]
    );

    const autocompleteMenuListExtras = useMemo(
        () => ({
            hasMoreData: resourceName ? hasMore : false,
            hasInfiniteLoader: !!resourceName,
            isDataLoading: isLoading,
            loadMore: async () => {
                if (hasLoadedOptions) {
                    await loadOptions(term);
                }
            },
            currentScrollPosition,
            setCurrentScrollPosition,
            isSelectedOptionHidden,
            selectedValue,
        }),
        [
            resourceName,
            hasMore,
            isLoading,
            hasLoadedOptions,
            loadOptions,
            term,
            currentScrollPosition,
            isSelectedOptionHidden,
            selectedValue,
        ]
    );

    const SearchSelectClearIndicator = useMemo(() => () => null, []);

    const selectComponents = useMemo(
        () => ({
            ValueContainer,
            ClearIndicator: SearchSelectClearIndicator,
            MenuList: AutocompleteSearchMenuList,
        }),
        [SearchSelectClearIndicator]
    );

    const selectWithSearchProps = useMemo(
        () => ({
            menuIsOpen: true,
            controlShouldRenderValue: false,
            autocompleteMenuListExtras,
            components: selectComponents,
            placeholder: isCreatable ? "Type to search or create new option" : "Search...",
            styles: {
                container: () => ({
                    display: isOpened ? "block" : "none",
                    width: additionalProps?.fieldWidth ?? "100%",
                    maxWidth: additionalProps?.maxFieldWidth,
                    position: "relative",
                }),
            },
            isClearable: false,
            isSearchable: true,
            // Focus the search input as soon as the select mounts while open.
            // In a modal the select lives in an MUI Popper that mounts its
            // children only after it opens, so the open-time focus effect below
            // runs before the input exists; react-select's own autoFocus covers
            // that path. Outside a modal the select stays mounted (hidden) and
            // the effect focuses it.
            autoFocus: isOpened,
            isValidNewOption: isCreatable ? undefined : () => false,
            onCreateOption: (inputValue) => {
                onChange(inputValue);
                applyActionOnCreate?.();
                isOpened && toggleOpened();
            },
            filterOption: null,
        }),
        [
            selectComponents,
            autocompleteMenuListExtras,
            isCreatable,
            isOpened,
            additionalProps?.fieldWidth,
            additionalProps?.maxFieldWidth,
            onChange,
            applyActionOnCreate,
            toggleOpened,
        ]
    );

    const dropdownProps = {
        uniqueKey,
        name,
        selectedValue,
        isOpened,
        toggleOpened,
        additionalProps,
        menuInModal,
        iconReplacement: options?.iconReplacement,
        clearValue: (e) => {
            e.stopPropagation();
            e.preventDefault();
            onChange(null);
            applyActionOnChange?.();
        },
    };

    const creatableSelectProps = {
        ...sharedProps,
        ...selectWithSearchProps,
        // In a modal the searchable select's whole custom-menu (search control +
        // option list) is anchored in an MUI Popper by `Dropdown` (see
        // ReactSelectComponents `Dropdown`). Render react-select's option menu
        // INLINE inside that Popper — not body-portaled — so the control and the
        // list stay one coherent, flip-aware panel instead of the control being
        // clipped by the dialog while the list floats detached below it. The
        // Popper (zIndex 2200) carries the modal-layer lift the menuPortal used
        // to provide.
        ...(menuInModal ? { menuPortalTarget: null, menuPosition: undefined } : {}),
        // Merge the two `styles` maps instead of letting selectWithSearchProps'
        // `styles` object replace sharedProps' wholesale. selectWithSearchProps
        // only overrides `container`; without this merge it also drops
        // sharedProps' in-modal `menuPortal` z-index lift for the non-modal
        // fallback path.
        styles: {
            ...sharedProps.styles,
            ...selectWithSearchProps.styles,
            // When hosted in the flip-aware MUI Popper (menuInModal), render
            // react-select's option list IN-FLOW (position:relative) beneath the
            // search control instead of absolutely (top:100%). An absolute list
            // sits outside the Popper's measured height, so the Popper's `flip`
            // modifier only ever sees the ~40px search control, judges the panel
            // fits in the gap below, and never flips — the detached list then
            // opens downward off-screen for a picker sitting near the viewport
            // bottom (the comment composer; Margot 2026-10-07). In-flow, the
            // Popper measures the real control+list height and flips the whole
            // panel into a true "dropup". Mirrors compactMenuInPopperProps' menu
            // style for the <5-option path.
            ...(menuInModal
                ? {
                      menu: () => ({
                          position: "relative" as const,
                          marginTop: 0,
                          width: "100%",
                      }),
                  }
                : {}),
        },
    };

    // Compact (<5 options, no search header per ui-design-principle-dropdowns-selects)
    // rendered INSIDE the Dropdown's flip-aware MUI Popper. For a picker that opts
    // into `menuInModal`/`menuPortal` (in a modal, or sitting at the viewport
    // bottom like the comment-assignment picker), the bare `SimpleSelect` relies on
    // react-select's own `menuPlacement:"auto"` with a fixed body portal, which
    // mis-places the menu ("opens a bit above, still spills down" — Margot,
    // 2026-10-07). Routing it through `DropdownContainer` gives it the SAME robust
    // MUI Popper (`flip` + `preventOverflow`) the >=5 path uses, so it opens as a
    // true "dropup" when there is no room below. The menu renders in-flow
    // (position:relative, not the body portal) so the Popper measures its real
    // height and flips the whole panel; the search control stays hidden to keep the
    // compact, searchless look the principle mandates for <5 options.
    const compactMenuInPopperProps = {
        ...sharedProps,
        menuIsOpen: true,
        controlShouldRenderValue: false,
        isSearchable: false,
        // Render react-select's menu inline inside the Popper, not body-portaled.
        menuPortalTarget: null,
        menuPosition: undefined,
        styles: {
            ...sharedProps.styles,
            container: () => ({
                display: isOpened ? "block" : "none",
                width: additionalProps?.fieldWidth ?? "100%",
                maxWidth: additionalProps?.maxFieldWidth,
                position: "relative" as const,
            }),
            // The DropdownContainer target IS the visible field; hide react-select's
            // own control so the panel shows only the compact option list.
            control: () => ({ display: "none" }),
            // In-flow (not absolute) so the MUI Popper measures the real panel
            // height and can flip it upward; flush to the Popper origin.
            menu: () => ({ position: "relative" as const, marginTop: 0, width: "100%" }),
        },
    };

    if (staticOptions !== undefined) {
        const groupOptions = group?.groupOptions !== undefined ? group.groupOptions : undefined;
        if (staticOptions.length < 5 && !isCreatable) {
            // A custom trigger (renderIconOnly) needs the DropdownContainer too:
            // the bare SimpleSelect below renders its own control and would drop
            // the caller's trigger whenever the list is short.
            if (menuInModal || additionalProps?.renderIconOnly) {
                return (
                    <DropdownContainer {...dropdownProps}>
                        <SimpleSelect
                            optionList={staticOptions}
                            selectedValue={selectedValue}
                            groupOptions={groupOptions}
                            {...compactMenuInPopperProps}
                        />
                    </DropdownContainer>
                );
            }
            return (
                <SimpleSelect
                    optionList={staticOptions}
                    selectedValue={selectedValue}
                    groupOptions={groupOptions}
                    {...sharedProps}
                />
            );
        }

        return (
            <DropdownContainer {...dropdownProps}>
                <SearchableSelect
                    optionList={staticOptions}
                    groupOptions={groupOptions}
                    selectedValue={selectedValue}
                    menuRef={menuRef}
                    defaultValue={defaultValue}
                    {...creatableSelectProps}
                />
            </DropdownContainer>
        );
    } else {
        return (
            <DropdownContainer {...dropdownProps}>
                <AsyncSelect
                    key={`${uniqueKey}-${isOpened}`}
                    menuRef={menuRef}
                    selectedValue={selectedValue}
                    loadOptions={loadOptions}
                    handleMenuOpen={handleMenuOpen}
                    currentPageRef={currentPageRef}
                    setTerm={setTerm}
                    dynamicOptions={dynamicOptions}
                    isSelectedOptionHidden={isSelectedOptionHidden}
                    {...creatableSelectProps}
                />
            </DropdownContainer>
        );
    }
};
